import React from "react";
import { Badge, Card, CardBody, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";

export type SendingSetupStepLetter = "A" | "B" | "C";

type Props = {
  stepDnsOk: boolean;
  stepSesOk: boolean;
  stepSslOk: boolean;
  trackingStatus: string;
  sesStatus: string;
  /** Clique no card → scroll até a seção (DNS / remetente / SSL). */
  onStepClick?: (step: SendingSetupStepLetter) => void;
};

function stepBadge(
  ok: boolean,
  pendingLabel: string,
  okLabel: string
): { color: string; label: string } {
  if (ok) return { color: "success", label: okLabel };
  return { color: "warning", label: pendingLabel };
}

/**
 * Progresso A/B/C: apontamento DNS → remetente SES → SSL do hostname.
 * Separados de propósito — Carla vê onde parou sem misturar (DOC 14 § 12).
 * Cards clicáveis levam à seção correspondente na mesma página.
 */
const SendingSetupProgress = ({
  stepDnsOk,
  stepSesOk,
  stepSslOk,
  trackingStatus,
  sesStatus,
  onStepClick,
}: Props) => {
  const { t } = useTranslation();

  const a = stepBadge(
    stepDnsOk,
    t("settings.sending.status.pending"),
    t("settings.sending.status.dns_ok")
  );
  const b = stepBadge(
    stepSesOk,
    t("settings.sending.status.pending"),
    t("settings.sending.status.verified")
  );

  // Passo C: ssl_ok ≠ active — cert emitido mas cadeado/SNI ainda não fechou.
  let c: { color: string; label: string };
  if (trackingStatus === "failed") {
    c = { color: "danger", label: t("settings.sending.status.failed") };
  } else if (trackingStatus === "active") {
    c = { color: "success", label: t("settings.sending.status.active") };
  } else if (trackingStatus === "ssl_ok") {
    c = { color: "info", label: t("settings.sending.status.ssl_ok") };
  } else {
    c = stepBadge(
      stepSslOk,
      t("settings.sending.status.pending"),
      t("settings.sending.status.ssl_ok")
    );
  }

  if (sesStatus === "failed") {
    b.color = "danger";
    b.label = t("settings.sending.status.failed");
  }

  const stepCDescription =
    trackingStatus === "ssl_ok"
      ? t("settings.sending.stepC.descriptionSslOk")
      : t("settings.sending.stepC.description");

  const steps: {
    letter: SendingSetupStepLetter;
    title: string;
    description: string;
    badge: { color: string; label: string };
  }[] = [
    {
      letter: "A",
      title: t("settings.sending.stepA.title"),
      description: t("settings.sending.stepA.description"),
      badge: a,
    },
    {
      letter: "B",
      title: t("settings.sending.stepB.title"),
      description: t("settings.sending.stepB.description"),
      badge: b,
    },
    {
      letter: "C",
      title: t("settings.sending.stepC.title"),
      description: stepCDescription,
      badge: c,
    },
  ];

  return (
    <Row className="g-3 mb-3 align-items-stretch">
      {steps.map((step) => {
        const interactive = Boolean(onStepClick);
        return (
          <Col md={4} key={step.letter} className="d-flex">
            {/* h-100 (não card-height-100): o calc do Velzon encolhe o card e o texto vaza no rodapé */}
            <Card
              className={`h-100 w-100 mb-0${interactive ? " border-primary-subtle" : ""}`}
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={
                interactive ? () => onStepClick?.(step.letter) : undefined
              }
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onStepClick?.(step.letter);
                      }
                    }
                  : undefined
              }
              style={interactive ? { cursor: "pointer" } : undefined}
              title={
                interactive
                  ? t("settings.sending.stepClickHint")
                  : undefined
              }
            >
              <CardBody className="pb-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="badge bg-primary-subtle text-primary">
                    {step.letter}
                  </span>
                  <Badge color={step.badge.color} pill>
                    {step.badge.label}
                  </Badge>
                </div>
                <h5 className="fs-15 mb-1">{step.title}</h5>
                <p className="text-muted mb-0 small">{step.description}</p>
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

export default SendingSetupProgress;
